import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Ticket, User } from '../../types';
import socketService from '../../services/socket';
import apiService from '../../services/api';
import { isTicketOpen } from '../../utils/statusUtils';

interface TicketTab {
  id: string;
  ticket: Ticket;
  hasNewMessages: boolean;
  lastMessageTime?: string;
  isNewlyAssigned?: boolean;
  assignedAt?: string;
}

interface PersistentTabBarProps {
  user: User | null;
  onTabClick: (ticket: Ticket) => void;
  activeTicketId: string | null;
}

export interface PersistentTabBarRef {
  refreshTabs: () => void;
}

const PersistentTabBar = forwardRef<PersistentTabBarRef, PersistentTabBarProps>(({
  user,
  onTabClick,
  activeTicketId
}, ref) => {
  const [openTabs, setOpenTabs] = useState<TicketTab[]>([]);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [pendingRefresh, setPendingRefresh] = useState(false);

  // Pagination state for scrollable tabs
  const [currentTabPage, setCurrentTabPage] = useState(0);
  const tabsPerPage = 8; // Show 8 tabs per page
  const totalPages = Math.ceil(openTabs.length / tabsPerPage);
  const canScrollLeft = currentTabPage > 0;
  const canScrollRight = currentTabPage < totalPages - 1;

  // Use refs to track current values without triggering re-renders
  const activeTicketIdRef = useRef<string | null>(activeTicketId);
  const openTabsRef = useRef<TicketTab[]>([]);
  const lastTicketCountRef = useRef<number>(0);
  
  // Track newly assigned tickets to show "N" indicator
  const newlyAssignedTicketsRef = useRef<Map<string, { assignedAt: string; source: 'claimed' | 'assigned' }>>(new Map());

  // Update refs when values change
  useEffect(() => {
    activeTicketIdRef.current = activeTicketId;
  }, [activeTicketId]);

  useEffect(() => {
    openTabsRef.current = openTabs;
  }, [openTabs]);

  // Scroll tabs left or right
  const scrollTabs = useCallback((direction: 'left' | 'right') => {
    console.log(`🎯 PersistentTabBar: scrollTabs called with direction: ${direction}, currentPage: ${currentTabPage}, totalPages: ${totalPages}`);
    if (direction === 'left' && canScrollLeft) {
      const newPage = Math.max(0, currentTabPage - 1);
      console.log(`🎯 PersistentTabBar: Navigating left from page ${currentTabPage} to ${newPage}`);
      setCurrentTabPage(newPage);
    } else if (direction === 'right' && canScrollRight) {
      const newPage = Math.min(totalPages - 1, currentTabPage + 1);
      console.log(`🎯 PersistentTabBar: Navigating right from page ${currentTabPage} to ${newPage}`);
      setCurrentTabPage(newPage);
    } else {
      console.log(`🎯 PersistentTabBar: Cannot navigate ${direction} - canScrollLeft: ${canScrollLeft}, canScrollRight: ${canScrollRight}`);
    }
  }, [currentTabPage, totalPages, canScrollLeft, canScrollRight]);

  // Load all claimed tickets for the agent
  const loadClaimedTickets = useCallback(async (source: string = 'manual') => {
    if (!user?.id) {
      console.log('🎯 PersistentTabBar: No user ID, skipping claimed tickets load');
      return;
    }

    console.log(`🎯 PersistentTabBar: Loading claimed tickets for user: ${user.id} (source: ${source})`);

    try {
      // Use apiService instead of direct fetch to ensure proper token handling
      console.log(`🔍 PersistentTabBar: Using apiService.getTickets()`);
      
      const response = await apiService.getTickets({ limit: 1000 }); // Get all tickets (high limit)
      const allTickets = response.tickets;
      
      console.log(`🎯 PersistentTabBar: Received all tickets: ${allTickets.length} (source: ${source})`);
      console.log(`🎯 PersistentTabBar: Sample ticket data:`, allTickets.slice(0, 3).map((t: Ticket) => ({ 
        id: t.id, 
        number: t.ticketNumber, 
        agentId: t.agentId, 
        status: t.status 
      })));
      
      // Filter for tickets assigned to current agent
      console.log(`🎯 PersistentTabBar: Filtering tickets for user ${user.id} (${user.firstName} ${user.lastName})`);
      console.log(`🎯 PersistentTabBar: User ID type: ${typeof user.id}, value: "${user.id}"`);
      console.log(`🎯 PersistentTabBar: All tickets:`, allTickets.map((t: Ticket) => ({ 
        id: t.id, 
        number: t.ticketNumber, 
        agentId: t.agentId, 
        agentIdType: typeof t.agentId,
        status: t.status
      })));
      
      // More flexible filtering - show tickets assigned to current agent (any status except closed)
      const claimed = allTickets.filter((ticket: Ticket) => {
        const isAssignedToUser = ticket.agentId === user.id;
        const isNotClosed = isTicketOpen(ticket.status);
        
        console.log(`🔍 Ticket ${ticket.ticketNumber}: agentId="${ticket.agentId}" (${typeof ticket.agentId}), userId="${user.id}" (${typeof user.id}), status=${ticket.status}, assigned=${isAssignedToUser}, notClosed=${isNotClosed}`);
        
        return isAssignedToUser && isNotClosed;
      });
        
        console.log(`🎯 PersistentTabBar: Found ${claimed.length} claimed tickets (source: ${source})`);
        console.log(`🎯 PersistentTabBar: Claimed tickets:`, claimed.map((t: Ticket) => ({ id: t.id, number: t.ticketNumber, agentId: t.agentId, status: t.status })));
        
        // Check if ticket count changed for immediate refresh detection
        const previousCount = lastTicketCountRef.current;
        const currentCount = claimed.length;
        lastTicketCountRef.current = currentCount;
        
        if (previousCount !== currentCount && source === 'auto') {
          console.log(`📊 PersistentTabBar: Ticket count changed from ${previousCount} to ${currentCount} - immediate update detected!`);
        }
        
        setLastRefreshTime(new Date());
        console.log(`✅ Loaded claimed tickets: ${claimed.length} (source: ${source})`);
        
        // Update tabs
        setOpenTabs(prevTabs => {
          console.log('🎯 PersistentTabBar: Creating tabs from claimed tickets:', claimed.map((t: Ticket) => ({ id: t.id, number: t.ticketNumber, agentId: t.agentId })));
          const newTabs: TicketTab[] = claimed.map((ticket: Ticket) => {
            // Check if tab already exists to preserve state
            const existingTab = prevTabs.find(tab => tab.id === ticket.id);
            
            // Check if this ticket should be marked as newly assigned
            const newlyAssignedInfo = newlyAssignedTicketsRef.current.get(ticket.id);
            const shouldMarkAsNewlyAssigned = !!newlyAssignedInfo; // Mark as newly assigned if in tracking map (regardless of existing tab)
            
            if (shouldMarkAsNewlyAssigned) {
              console.log(`🆕 PersistentTabBar: Marking ticket ${ticket.id} as newly assigned (${newlyAssignedInfo.source}) - existing tab: ${!!existingTab}`);
            }
            
            if (existingTab) {
              // Preserve existing tab state, but update newly assigned status if applicable
              console.log(`🔄 PersistentTabBar: Preserving existing tab state for ${ticket.id}:`, {
                hasNewMessages: existingTab.hasNewMessages,
                isNewlyAssigned: existingTab.isNewlyAssigned,
                willMarkAsNewlyAssigned: shouldMarkAsNewlyAssigned,
                source: source
              });
              
              // For force_refresh and other non-socket sources, preserve ALL existing indicator states
              // For socket events, update indicators appropriately
              const preserveExistingIndicators = source === 'force_refresh' || source === 'auto_refresh' || source === 'visibility_change' || source === 'window_focus';
              
              return {
                ...existingTab,
                ticket: ticket, // Update with latest ticket data
                // Preserve indicators during auto-refresh, but allow socket events to update them
                hasNewMessages: preserveExistingIndicators ? existingTab.hasNewMessages : existingTab.hasNewMessages,
                isNewlyAssigned: preserveExistingIndicators ? (existingTab.isNewlyAssigned || shouldMarkAsNewlyAssigned) : (shouldMarkAsNewlyAssigned || existingTab.isNewlyAssigned),
                assignedAt: newlyAssignedInfo?.assignedAt || existingTab.assignedAt,
                lastMessageTime: existingTab.lastMessageTime
              };
            } else {
              // New tab - initialize with default state
              return {
                id: ticket.id,
                ticket: ticket,
                hasNewMessages: false,
                isNewlyAssigned: shouldMarkAsNewlyAssigned,
                assignedAt: newlyAssignedInfo?.assignedAt
              };
            }
          });
          
          console.log('🎯 PersistentTabBar: Setting new tabs:', newTabs.map((t: TicketTab) => ({ 
            id: t.id, 
            number: t.ticket.ticketNumber, 
            hasNewMessages: t.hasNewMessages,
            isNewlyAssigned: t.isNewlyAssigned 
          })));

          // Join socket rooms for all claimed tickets so we receive new_message events
          const allTicketIds = newTabs.map(tab => tab.id);
          if (allTicketIds.length > 0) {
            console.log('📝 PersistentTabBar: Joining socket rooms for claimed tickets:', allTicketIds);
            socketService.joinClaimedTicketRooms(allTicketIds);
          }

          return newTabs;
        });
    } catch (error) {
      console.error('❌ PersistentTabBar: Error loading claimed tickets:', error);
      
      // Handle authentication errors
      if (error instanceof Error && error.message.includes('Authentication failed')) {
        console.log('🔒 PersistentTabBar: Authentication failed - user may need to log in again');
        // Don't clear tabs immediately - let AuthContext handle logout
      }
      
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('🌐 PersistentTabBar: Network error - backend may not be running');
      }
      
      // Keep existing tabs on error to maintain state
      console.log('⚠️ PersistentTabBar: Keeping existing tabs due to API error');
    }
  }, [user?.id, user?.firstName, user?.lastName]);

  // Load claimed tickets on mount and when user changes
  useEffect(() => {
    console.log('🎯 PersistentTabBar: useEffect triggered', { 
      user: user?.id, 
      userName: user?.firstName, 
      userRole: user?.role 
    });
    if (user?.id) {
      console.log('🎯 PersistentTabBar: User found, loading claimed tickets');
      console.log('🎯 PersistentTabBar: User object:', JSON.stringify(user, null, 2));
      loadClaimedTickets('initial');
    } else {
      console.log('🎯 PersistentTabBar: No user or user.id, skipping load');
      console.log('🎯 PersistentTabBar: User data:', user);
      // Clear tabs and newly assigned tracking when user logs out
      setOpenTabs([]);
      newlyAssignedTicketsRef.current.clear();
      // Clear all claimed ticket rooms when user logs out
      socketService.clearClaimedTicketRooms();
    }
  }, [user, loadClaimedTickets]);

  // Set up automatic refresh interval
  useEffect(() => {
    if (!user?.id) return;

    console.log('🎯 PersistentTabBar: Setting up automatic refresh interval');
    
    // Force refresh tabs every 5 seconds without any conditions or user prompts
    const refreshInterval = setInterval(() => {
      console.log('🔄 PersistentTabBar: Force refresh triggered (every 5 seconds)');
      loadClaimedTickets('force_refresh');
    }, 5000); // 5 seconds - force refresh every 5 seconds without asking

    // Cleanup interval on unmount or user change
    return () => {
      console.log('🎯 PersistentTabBar: Cleaning up refresh interval');
      clearInterval(refreshInterval);
    };
  }, [user?.id, loadClaimedTickets]);

  // High-frequency refresh when pending changes detected
  useEffect(() => {
    if (!user?.id || !pendingRefresh) return;

    console.log('🚀 PersistentTabBar: Starting high-frequency refresh due to pending changes');
    
    // When there are pending changes, refresh every 500ms for 10 seconds
    let attempts = 0;
    const maxAttempts = 20; // 10 seconds at 500ms intervals
    
    const highFrequencyInterval = setInterval(() => {
      attempts++;
      console.log(`🚀 PersistentTabBar: High-frequency refresh attempt ${attempts}/${maxAttempts}`);
      loadClaimedTickets(`high_freq_${attempts}`);
      
      if (attempts >= maxAttempts) {
        console.log('🚀 PersistentTabBar: Max high-frequency attempts reached, stopping');
        setPendingRefresh(false);
        clearInterval(highFrequencyInterval);
      }
    }, 500); // 500ms intervals

    return () => {
      console.log('🚀 PersistentTabBar: Cleaning up high-frequency refresh interval');
      clearInterval(highFrequencyInterval);
    };
  }, [user?.id, pendingRefresh, loadClaimedTickets]);

  // Refresh tabs when browser tab becomes visible again or gains focus
  useEffect(() => {
    if (!user?.id) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🎯 PersistentTabBar: Browser tab became visible, refreshing tabs');
        loadClaimedTickets('visibility_change');
      }
    };

    const handleWindowFocus = () => {
      console.log('🎯 PersistentTabBar: Window gained focus, force refreshing tabs');
      loadClaimedTickets('window_focus');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    
    // Listen for custom ticket events for immediate refresh
    const handleCustomTicketClaimed = (event: CustomEvent) => {
      console.log('🎯 PersistentTabBar: Received custom ticket-claimed event:', event.detail);
      if (event.detail.agentId === user?.id) {
        console.log('✅ PersistentTabBar: Custom claim event for current user, immediate refresh');
        loadClaimedTickets('custom_claim_event');
      }
    };

    const handleCustomTicketUnclaimed = (event: CustomEvent) => {
      console.log('🎯 PersistentTabBar: Received custom ticket-unclaimed event:', event.detail);
      if (event.detail.agentId === user?.id) {
        console.log('✅ PersistentTabBar: Custom unclaim event for current user, immediate refresh');
        loadClaimedTickets('custom_unclaim_event');
      }
    };

    window.addEventListener('ticket-claimed', handleCustomTicketClaimed as EventListener);
    window.addEventListener('ticket-unclaimed', handleCustomTicketUnclaimed as EventListener);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('ticket-claimed', handleCustomTicketClaimed as EventListener);
      window.removeEventListener('ticket-unclaimed', handleCustomTicketUnclaimed as EventListener);
    };
  }, [user?.id, loadClaimedTickets]);

  // Socket listeners for immediate tab updates
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    console.log('🎯 PersistentTabBar: Setting up socket listeners for user:', user.id);

    // Listen for ticket assignments
    const handleTicketAssigned = (data: { ticketId: string; agentId: string; agentName?: string; assignedAt: string; previousAgent?: string }) => {
      console.log('🎯 PersistentTabBar: Received ticket_assigned event:', data);
      
      // If the ticket was assigned to current user, refresh tabs immediately
      if (data.agentId === user.id) {
        console.log('✅ PersistentTabBar: Ticket assigned to current user, refreshing tabs');
        
        // Join the ticket room so we can receive new_message events
        console.log('📝 PersistentTabBar: Joining room for newly assigned ticket:', data.ticketId);
        socketService.joinClaimedTicketRooms([data.ticketId]);
        
        // Always mark as newly assigned when assigned to current user (whether new tab or existing)
        console.log('🆕 PersistentTabBar: Marking ticket as newly assigned via assignment:', data.ticketId);
        newlyAssignedTicketsRef.current.set(data.ticketId, {
          assignedAt: data.assignedAt,
          source: 'assigned'
        });
        
        // Trigger high-frequency refresh mode
        console.log('🚀 PersistentTabBar: Triggering high-frequency refresh for assignment');
        setPendingRefresh(true);
        
        // Immediate and multiple refresh attempts to ensure the tab appears
        loadClaimedTickets('socket_assigned');
        
        // Rapid refresh burst for immediate visibility
        for (let i = 0; i < 10; i++) {
          setTimeout(() => {
            console.log(`🔄 PersistentTabBar: Rapid refresh ${i + 1}/10 after assignment`);
            loadClaimedTickets(`socket_assigned_rapid_${i + 1}`);
          }, i * 100); // Every 100ms for 1 second
        }
      }
      // If the ticket was unassigned from current user, also refresh tabs
      else if (data.previousAgent === user.id) {
        console.log('✅ PersistentTabBar: Ticket unassigned from current user, refreshing tabs');
        
        // Leave the ticket room since we're no longer assigned
        console.log('📝 PersistentTabBar: Leaving room for unassigned ticket:', data.ticketId);
        socketService.leaveClaimedTicketRoom(data.ticketId);
        
        // Remove from newly assigned tracking if it was there
        newlyAssignedTicketsRef.current.delete(data.ticketId);
        loadClaimedTickets('socket_unassigned');
      }
    };

    // Listen for ticket claims
    const handleTicketClaimed = (data: { ticketId: string; agent: any; claimedAt: string; handoffFromAI?: boolean; autoClaimed?: boolean }) => {
      console.log('🎯 PersistentTabBar: Received ticket_claimed event:', data);
      
      // If the ticket was claimed by current user, refresh tabs immediately
      if (data.agent?.id === user.id) {
        console.log('✅ PersistentTabBar: Ticket claimed by current user, refreshing tabs');
        
        // Join the ticket room so we can receive new_message events
        console.log('📝 PersistentTabBar: Joining room for newly claimed ticket:', data.ticketId);
        socketService.joinClaimedTicketRooms([data.ticketId]);
        
        // Always mark as newly assigned when claimed by current user (whether new tab or existing)
        console.log('🆕 PersistentTabBar: Marking ticket as newly assigned via claim:', data.ticketId);
        newlyAssignedTicketsRef.current.set(data.ticketId, {
          assignedAt: data.claimedAt,
          source: 'claimed'
        });
        
        // Trigger high-frequency refresh mode
        console.log('🚀 PersistentTabBar: Triggering high-frequency refresh for claim');
        setPendingRefresh(true);
        
        // Immediate and multiple refresh attempts to ensure the tab appears
        loadClaimedTickets('socket_claimed');
        
        // Rapid refresh burst for immediate visibility
        for (let i = 0; i < 10; i++) {
          setTimeout(() => {
            console.log(`🔄 PersistentTabBar: Rapid refresh ${i + 1}/10 after claim`);
            loadClaimedTickets(`socket_claimed_rapid_${i + 1}`);
          }, i * 100); // Every 100ms for 1 second
        }
      }
    };

    // Listen for ticket updates (could include assignment changes)
    const handleTicketUpdated = (data: any) => {
      console.log('🎯 PersistentTabBar: Received ticket_updated event:', data);
      
      // Handle both possible data structures from backend
      let ticketId: string;
      let newAgentId: string | null;
      let updatedAt: string;
      
      if (data.ticketId && data.updates) {
        // New structure: {ticketId, updates, updatedBy, updatedAt}
        ticketId = data.ticketId;
        
        // Check if this is an agent assignment update
        const hasAgentUpdate = 'agentId' in data.updates || 'agent_id' in data.updates;
        if (!hasAgentUpdate) {
          console.log('🎯 PersistentTabBar: No agent assignment in update, skipping');
          return;
        }
        
        // Get the new agent ID from updates (could be agentId or agent_id)
        newAgentId = data.updates.agentId || data.updates.agent_id;
        updatedAt = data.updatedAt || new Date().toISOString();
      } else if (data.id && typeof data.agentId !== 'undefined') {
        // Old structure: Ticket object
        ticketId = data.id;
        newAgentId = data.agentId;
        updatedAt = data.updatedAt || new Date().toISOString();
      } else {
        console.log('🎯 PersistentTabBar: Unknown ticket_updated structure, skipping:', data);
        return;
      }
      
      // Check if this affects current user's assignments
      const isCurrentlyAssignedToUser = newAgentId === user.id;
      const existingTab = openTabsRef.current.find(tab => tab.id === ticketId);
      const wasInTabs = !!existingTab;
      
      console.log('🎯 PersistentTabBar: Assignment check:', {
        ticketId,
        currentAgentId: newAgentId,
        userId: user.id,
        isCurrentlyAssignedToUser,
        wasInTabs,
        dataStructure: data.ticketId ? 'new' : 'old'
      });
      
      // Check if this is a newly assigned ticket (assigned to current user)
      if (isCurrentlyAssignedToUser) {
        console.log('🆕 PersistentTabBar: Ticket newly assigned to current user via update event:', ticketId, '- was in tabs:', wasInTabs);
        newlyAssignedTicketsRef.current.set(ticketId, {
          assignedAt: updatedAt,
          source: 'assigned'
        });
      }
      // If ticket was unassigned from current user, remove from newly assigned tracking
      else if (!isCurrentlyAssignedToUser && wasInTabs) {
        console.log('🗑️ PersistentTabBar: Ticket unassigned from current user, removing from tracking:', ticketId);
        newlyAssignedTicketsRef.current.delete(ticketId);
      }
      
      // Refresh tabs if:
      // 1. Ticket is now assigned to current user (should appear in tabs)
      // 2. Ticket was in tabs but is no longer assigned to current user (should disappear)
      // 3. Ticket was already in tabs and still assigned to user (update tab info)
      if (isCurrentlyAssignedToUser || wasInTabs) {
        console.log('✅ PersistentTabBar: Ticket assignment affects current user, refreshing tabs');
        loadClaimedTickets('socket_updated');
      }
    };

    // Listen for new messages to update tab indicators
    const handleNewMessage = (data: { message: any; forIndicators?: boolean }) => {
      console.log('🔔 PersistentTabBar: Received new_message event:', {
        ticketId: data.message.ticketId,
        senderType: data.message.sender?.userType,
        content: data.message.content?.substring(0, 50) + '...'
      });
      
      // Show indicator for customer messages
      if (data.message.sender?.userType === 'customer' || data.message.sender?.userType === 'system') {
        // Check if we have this ticket in our claimed tabs
        const hasTab = openTabsRef.current.find(tab => tab.id === data.message.ticketId);
        
        console.log('🔴 PersistentTabBar: Customer/system message for ticket:', data.message.ticketId, 'hasTab:', hasTab ? 'YES' : 'NO');
        
        if (hasTab) {
          setOpenTabs(prevTabs => {
            const updatedTabs = prevTabs.map(tab => 
              tab.id === data.message.ticketId 
                ? { ...tab, hasNewMessages: true, lastMessageTime: data.message.createdAt }
                : tab
            );
            console.log('🔴 PersistentTabBar: Updated tabs with new message indicator:', updatedTabs.map(t => ({ 
              id: t.id, 
              number: t.ticket.ticketNumber, 
              hasNewMessages: t.hasNewMessages 
            })));
            return updatedTabs;
          });
        } else {
          console.log('🔴 PersistentTabBar: Message for ticket not in claimed tabs, ignoring');
        }
      } else {
        console.log('🔔 PersistentTabBar: Ignoring non-customer message from:', data.message.sender?.userType);
      }
    };

    // Listen for AI status changes to refresh tabs
    const handleAiStatusChange = (data: { ticketId: string; enabled: boolean; reason?: string; changedBy: string }) => {
      console.log('🤖 PersistentTabBar: Received ai_status_changed event:', data);
      
      // Check if this affects any of our claimed tickets
      const affectedTab = openTabsRef.current.find(tab => tab.id === data.ticketId);
      if (affectedTab) {
        console.log('✅ PersistentTabBar: AI status change affects claimed ticket, refreshing tabs');
        loadClaimedTickets('socket_ai_changed');
      }
    };

    // Set up socket listeners
    socketService.on('ticket_assigned', handleTicketAssigned);
    socketService.on('ticket_claimed', handleTicketClaimed);
    socketService.on('ticket_updated', handleTicketUpdated);
    socketService.on('new_message', handleNewMessage);
    socketService.on('ai_status_changed', handleAiStatusChange);

    // Cleanup function
    return () => {
      console.log('🎯 PersistentTabBar: Cleaning up socket listeners');
      socketService.off('ticket_assigned', handleTicketAssigned);
      socketService.off('ticket_claimed', handleTicketClaimed);
      socketService.off('ticket_updated', handleTicketUpdated);
      socketService.off('new_message', handleNewMessage);
      socketService.off('ai_status_changed', handleAiStatusChange);
    };
  }, [user?.id, loadClaimedTickets, activeTicketId]);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refreshTabs: () => {
      console.log('🎯 PersistentTabBar: External refresh triggered');
      if (user?.id) {
        loadClaimedTickets('external_refresh');
      }
    }
  }), [user?.id, loadClaimedTickets]);

  // Handle tab click
  const handleTabClick = (tab: TicketTab) => {
    console.log('🎯 PersistentTabBar: Tab clicked:', tab.ticket.ticketNumber);
    console.log('🎯 PersistentTabBar: Tab indicators before click:', {
      hasNewMessages: tab.hasNewMessages,
      isNewlyAssigned: tab.isNewlyAssigned
    });
    
    // Clear newly assigned flag from tracking
    if (newlyAssignedTicketsRef.current.has(tab.id)) {
      console.log('🧹 PersistentTabBar: Clearing newly assigned flag for clicked tab:', tab.id);
      newlyAssignedTicketsRef.current.delete(tab.id);
    }
    
    // Clear indicators when clicking on tab
    setOpenTabs(prevTabs => 
      prevTabs.map(t => 
        t.id === tab.id 
          ? { 
              ...t, 
              hasNewMessages: false, 
              isNewlyAssigned: false,
              lastMessageTime: t.lastMessageTime // Preserve timestamp
            }
          : t
      )
    );
    
    console.log('🧹 PersistentTabBar: Cleared indicators for tab:', tab.id);
    
    // Call parent handler
    onTabClick(tab.ticket);
  };

  // Handle close tab
  const handleCloseTab = async (tab: TicketTab, event: React.MouseEvent) => {
    event.stopPropagation();

    // Confirm with user before unclaiming
    const displayName = tab.ticket.customerName || tab.ticket.ticketNumber;
    if (window.confirm(`Are you sure you want to unclaim "${displayName}"? This will remove it from your tabs.`)) {
      try {
        // Clear newly assigned flag from tracking
        if (newlyAssignedTicketsRef.current.has(tab.id)) {
          console.log('🧹 PersistentTabBar: Clearing newly assigned flag for unclaimed tab:', tab.id);
          newlyAssignedTicketsRef.current.delete(tab.id);
        }
        
        // Leave the claimed ticket room since we're unclaiming
        console.log('📝 PersistentTabBar: Leaving claimed ticket room for unclaimed ticket:', tab.id);
        socketService.leaveClaimedTicketRoom(tab.id);
        
        // Unclaim the ticket by setting agentId to null
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/tickets/${tab.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            agentId: null,
            status: 'new'
          })
        });

        if (response.ok) {
          console.log('✅ Ticket unclaimed successfully');
          
          // Emit custom event for immediate tab bar refresh
          window.dispatchEvent(new CustomEvent('ticket-unclaimed', {
            detail: { ticketId: tab.id, agentId: user?.id }
          }));
          
          // Immediate refresh after unclaim
          setTimeout(() => loadClaimedTickets('unclaim'), 100);
        } else {
          console.error('❌ Failed to unclaim ticket');
          alert('Failed to unclaim ticket. Please try again.');
        }
      } catch (error) {
        console.error('❌ Error unclaiming ticket:', error);
        alert('Error unclaiming ticket. Please try again.');
      }
    }
  };

  // Calculate current tabs to display
  const startIndex = currentTabPage * tabsPerPage;
  const currentTabs = openTabs.slice(startIndex, startIndex + tabsPerPage);

  console.log('🎯 PersistentTabBar render:', {
    openTabsCount: openTabs.length,
    currentTabsCount: currentTabs.length,
    currentTabPage,
    totalPages,
    canScrollLeft,
    canScrollRight,
    activeTicketId,
    user: user?.id,
    openTabs: openTabs.map((t: TicketTab) => ({ id: t.id, number: t.ticket.ticketNumber }))
  });

  return (
    <div className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          {/* Left Scroll Arrow */}
          {openTabs.length > 0 && canScrollLeft && (
            <button
              onClick={(e) => {
                e.preventDefault();
                console.log('🎯 PersistentTabBar: Left arrow button clicked');
                scrollTabs('left');
              }}
              className="flex-shrink-0 p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors border border-blue-200 dark:border-blue-700"
              title="Navigate to previous page of tabs (Ctrl+Left)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          
          <div className="flex items-center space-x-1 flex-1 min-w-0">
            {openTabs.length === 0 ? (
              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 px-3 py-2">
                <span>No claimed tickets. Claim a ticket to see it here.</span>
              </div>
            ) : (
              currentTabs.map((tab, index) => {
                // Calculate the actual index in the full tabs array for keyboard shortcuts
                const actualIndex = currentTabPage * tabsPerPage + index;
                return (
                  <div
                    key={tab.id}
                    data-tab-id={tab.id}
                    onClick={() => handleTabClick(tab)}
                    title={tab.hasNewMessages ? `${tab.ticket.ticketNumber} - New message received!` : tab.isNewlyAssigned ? `${tab.ticket.ticketNumber} - Newly assigned to you!` : `${tab.ticket.ticketNumber} - ${tab.ticket.customerName || 'Customer'}${tab.ticket.customerCompany ? ` (${tab.ticket.customerCompany})` : ''}`}
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-md cursor-pointer transition-all duration-200 min-w-0 max-w-48
                      ${tab.hasNewMessages
                        ? 'bg-red-500 dark:bg-red-600 text-white border border-red-600 dark:border-red-500 shadow-lg animate-pulse'
                        : tab.isNewlyAssigned
                        ? 'bg-green-500 dark:bg-green-600 text-white border border-green-600 dark:border-green-500 shadow-lg animate-bounce'
                        : tab.id === activeTicketId
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {/* Tab content */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${tab.hasNewMessages || tab.isNewlyAssigned ? 'font-bold text-white' : 'font-medium'}`}>
                        {tab.ticket.customerName || tab.ticket.ticketNumber}
                      </div>
                      <div className={`text-xs truncate ${tab.hasNewMessages ? 'text-red-100 font-medium' : tab.isNewlyAssigned ? 'text-green-100 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                        {tab.ticket.customerCompany ? `${tab.ticket.customerCompany} • ${tab.ticket.ticketNumber}` : tab.ticket.ticketNumber}
                      </div>
                    </div>

                    {/* Keyboard shortcut indicator */}
                    {actualIndex < 8 && (
                      <div className={`text-xs flex-shrink-0 rounded px-1 ${
                        tab.hasNewMessages 
                          ? 'text-red-100 bg-red-600 dark:bg-red-700' 
                          : tab.isNewlyAssigned
                          ? 'text-green-100 bg-green-600 dark:bg-green-700'
                          : 'text-gray-400 dark:text-gray-500 bg-gray-200 dark:bg-gray-600'
                      }`} title={`Ctrl+${actualIndex + 1}`}>
                        {actualIndex + 1}
                      </div>
                    )}

                    {/* Close button */}
                    <button
                      onClick={(e) => handleCloseTab(tab, e)}
                      className={`w-4 h-4 flex items-center justify-center rounded flex-shrink-0 ${
                        tab.hasNewMessages
                          ? 'text-red-100 hover:text-white hover:bg-red-600 dark:hover:bg-red-700'
                          : tab.isNewlyAssigned
                          ? 'text-green-100 hover:text-white hover:bg-green-600 dark:hover:bg-green-700'
                          : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      title={tab.id === activeTicketId ? `Unclaim ticket ${tab.ticket.ticketNumber} (Ctrl+U)` : `Unclaim ticket ${tab.ticket.ticketNumber}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Right Scroll Arrow */}
          {openTabs.length > 0 && canScrollRight && (
            <button
              onClick={(e) => {
                e.preventDefault();
                console.log('🎯 PersistentTabBar: Right arrow button clicked');
                scrollTabs('right');
              }}
              className="flex-shrink-0 p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors border border-blue-200 dark:border-blue-700"
              title="Navigate to next page of tabs (Ctrl+Right)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          
          {/* Page indicator */}
          {openTabs.length > 0 && totalPages > 1 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 px-2">
              {currentTabPage + 1}/{totalPages}
            </div>
          )}
        </div>
        
        {/* Control panel */}
        <div className="flex items-center space-x-4 ml-4">
          <div className="text-xs text-gray-400">
            {openTabs.length} tabs
            {lastRefreshTime && ` | ${lastRefreshTime.toLocaleTimeString()}`}
          </div>
        </div>
      </div>
    </div>
  );
});

export default PersistentTabBar; 